import * as React from 'react';
import { observer } from 'mobx-react';
import { BudgetConfigurationViewStore } from 'nomst/Stores/Budget/budgetConfigurationView.store';
import { formatDate, dateTextFormat } from 'utils/dateUtils';
import BudgetSettingsModal from 'components/BudgetConfiguration/budgetSettingsModal';
import EditBudgetView from 'components/BudgetConfiguration/editBudgetView';
import ConfirmationModal from 'components/Common/confirmationModal';
import BudgetBox from 'components/BudgetConfiguration/budgetBox';
import { EligibilityProrationSettings } from 'nomst/Components/EligibilityProration/eligibilityProrationSettings';
import { configurationsStore } from 'utils/MobxDataManager/singletons';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';
import { ICycleBudget } from 'typings/cycleBudget';
import { Segmented, Alert } from '@payscale/design';
import './budgetConfigurationView.scss';

export interface IBudgetConfigurationViewProps {
    budgetConfigurationViewStore: BudgetConfigurationViewStore;
    readonly?: boolean;
}

@observer
export default class BudgetConfigurationView
    extends React.Component<IBudgetConfigurationViewProps, {}> {

    constructor(props: IBudgetConfigurationViewProps) {
        super(props);
        this.onCloseDeleteBudgetConfirmation = this.onCloseDeleteBudgetConfirmation.bind(this);
        this.onConfirmDeleteBudgetConfirmation = this.onConfirmDeleteBudgetConfirmation.bind(this);
        this.onClickOpenCreateBudget = this.onClickOpenCreateBudget.bind(this);
        this.onChangeMultiCountry = this.onChangeMultiCountry.bind(this);
        this.openEligibilityProrationSlideout = this.openEligibilityProrationSlideout.bind(this);
        this.closeEligibilityProrationSlideout = this.closeEligibilityProrationSlideout.bind(this);
    }

    render(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!configurationsStore.convertedCycleSettingsViewCycle) {
            return (<div />);
        }

        return (
            <div className="col-md-10 col-lg-9 pxl-padding-xlarge">
                {this.renderConfigurationContent()}
                <BudgetSettingsModal budgetConfigurationViewStore={budgetConfigurationViewStore} />
                {this.renderDeleteBudgetConfirmationModal()}
            </div>
        );
    }

    renderConfigurationContent(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (budgetConfigurationViewStore.currentSetBudget) {
            return this.renderEditBudgetView();
        }

        return this.renderBudgetConfigurationList();
    }

    renderBudgetConfigurationList(): React.ReactNode {
        const { budgetConfigurationViewStore, readonly } = this.props;
        const { eligibilityProrationRuleView } = budgetConfigurationViewStore;
        const { convertedCycleSettingsViewCycle } = configurationsStore;
        if (!convertedCycleSettingsViewCycle.cycleId) {
            return null;
        }
        return (
            <div className="budget-config">
                <div className="flex-heading">
                    <hgroup>
                        <h1 className="pxl-header-sm budget-config__header">Budgets</h1>
                        <span className="pxl-text-xs">
                            Effective on {formatDate(convertedCycleSettingsViewCycle.payEffectiveDate, dateTextFormat)}
                        </span>
                    </hgroup>
                    <div>
                        <div className="budget-config__multi-country">
                            <label htmlFor="multiCountrySegmented" className="pxl-segmented-label pxl-text--sm">Group Employees by Country
                            &nbsp;
                            <Segmented
                                className="pxl-segmented--secondary budget-config__multi-country-button"
                                value={convertedCycleSettingsViewCycle?.teamPageViews?.multiCountry}
                                id="multiCountrySegmented"
                                onChange={this.onChangeMultiCountry}
                                options={[{ text: 'On', value: true }, { text: 'Off', value: false }]}
                            />
                            </label>
                        </div>
                        <div className="pxl-text-right">
                            <button
                                onClick={this.openEligibilityProrationSlideout}
                                className="pxl-text--sm pxl-link"
                            >
                                Eligibility &amp; Proration Rules
                            </button>
                        </div>
                        <EligibilityProrationSettings
                            eligibilityProrationRuleView={eligibilityProrationRuleView}
                            isOpen={eligibilityProrationRuleView.showEligibilityProrationRuleDialog}
                            closeSlideout={this.closeEligibilityProrationSlideout}
                        />
                    </div>
                </div>
                {this.renderBudgetWarning()}
                <div className="flex-row">
                    {convertedCycleSettingsViewCycle.cycleBudgets?.map((budget, index) => this.renderBudgetBox(budget, index))}
                    <div
                        hidden={readonly}
                        className="new-tile"
                    >
                        <button className="pxl-link" onClick={this.onClickOpenCreateBudget}>
                            <i className="icon-border icon-circle-thin">
                                <i className="icon-plus" />
                            </i>
                            <span>Create New</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderBudgetBox(budget: ICycleBudget, index: number): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        return (
            <BudgetBox
                key={index}
                budgetConfigurationViewStore={budgetConfigurationViewStore}
                budget={new CycleBudget(budget)}
                id={index}
            />
        );
    }

    renderDeleteBudgetConfirmationModal(): React.ReactNode {
        const { budgetConfigurationViewStore } = this.props;
        if (!budgetConfigurationViewStore.showDeleteBudgetConfirmation) {
            return null;
        }

        const budget = budgetConfigurationViewStore.currentSetBudget;
        return (
            <ConfirmationModal
                title="Delete Budget"
                message={this.renderDeleteBudgetConfirmationMessage(budget ?? {} as CycleBudget)}
                onClose={this.onCloseDeleteBudgetConfirmation}
                onCancel={this.onCloseDeleteBudgetConfirmation}
                onConfirm={this.onConfirmDeleteBudgetConfirmation}
            />
        );
    }

    renderDeleteBudgetConfirmationMessage(budget: CycleBudget): React.ReactNode {
        return (<div>Are you sure you want to delete <strong>{budget.name}</strong> budget?</div>);
    }

    private renderBudgetWarning(): React.ReactNode {
        if (configurationsStore.convertedCycleSettingsViewCycle?.cycleBudgets?.length) {
            return null;
        }
        return (
            <Alert
                message={'At least one budget must be created to open the cycle'}
                level="info"
            />

        );
    }

    private openEligibilityProrationSlideout(): void {
        this.props.budgetConfigurationViewStore.eligibilityProrationRuleView.openRuleDialogGlobal();
    }

    private closeEligibilityProrationSlideout(): void {
        this.props.budgetConfigurationViewStore.eligibilityProrationRuleView.closeRuleDialogGlobal();
    }

    private onCloseDeleteBudgetConfirmation(): void {
        this.props.budgetConfigurationViewStore.closeDeleteBudgetConfirmationModal();
    }

    private onConfirmDeleteBudgetConfirmation(): Promise<void> {
        const { budgetConfigurationViewStore } = this.props;
        const budget = budgetConfigurationViewStore.currentSetBudget;
        if (!budget) {
            return Promise.resolve();
        }
        return this.props.budgetConfigurationViewStore.deleteBudget(budget.budgetCycleId);
    }

    private onClickOpenCreateBudget(): void {
        this.props.budgetConfigurationViewStore.openCreateBudgetModal();
    }

    private async onChangeMultiCountry(_: undefined, value: boolean): Promise<void> {
        configurationsStore.convertedCycleSettingsViewCycle.teamPageViews.setMultiCountry(value);
        const currentCycleConfig = configurationsStore.cycleSettingsViewCycle;
        if (!currentCycleConfig) {
            return;
        }
        currentCycleConfig.teamPageViews.multiCountry = value;
        await configurationsStore.saveCycleConfiguration(currentCycleConfig);
    }

    private renderEditBudgetView(): React.ReactNode {
        const { budgetConfigurationViewStore, readonly } = this.props;
        return (
            <EditBudgetView
                budget={budgetConfigurationViewStore.currentSetBudget ?? {} as CycleBudget}
                readonly={readonly}
            />
        );
    }
}